import { create } from 'zustand';

/**
 * Reading Tempo Store — tracks study completions during the current browser
 * session so the radiologist can see their reading pace at a glance.
 *
 * Completions are stored in sessionStorage so a page refresh doesn't reset
 * mid-shift. Closing the browser tab starts a fresh session.
 *
 * A "completion" is recorded when a study transitions to a terminal reading
 * status: DICTATED, READ, TRANSCRIBED, REPORTED, or via CompleteStudy.
 */

const SESSION_KEY = 'brit_reading_tempo';

interface Completion {
  accession: string;
  timestamp: number;
}

interface PersistedState {
  sessionStartMs: number;
  completions: Completion[];
}

export interface ReadingTempoState {
  sessionStartMs: number;
  completions: Completion[];

  /** Record that a study was completed (read/dictated/reported). De-dupes by accession. */
  recordCompletion: (accession: string) => void;

  /** Reset the session (e.g. user explicitly resets). */
  reset: () => void;
}

function loadPersisted(): PersistedState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedState;
      if (parsed.sessionStartMs && Array.isArray(parsed.completions)) {
        return parsed;
      }
    }
  } catch {
    // corrupt — start fresh
  }
  return { sessionStartMs: Date.now(), completions: [] };
}

function persist(state: PersistedState) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable — non-critical
  }
}

export const useReadingTempoStore = create<ReadingTempoState>((set, get) => {
  const initial = loadPersisted();

  return {
    sessionStartMs: initial.sessionStartMs,
    completions: initial.completions,

    recordCompletion: (accession: string) => {
      const state = get();
      // De-dupe: don't count the same accession twice
      if (state.completions.some((c) => c.accession === accession)) return;

      const updated = [...state.completions, { accession, timestamp: Date.now() }];
      persist({ sessionStartMs: state.sessionStartMs, completions: updated });
      set({ completions: updated });
    },

    reset: () => {
      const fresh: PersistedState = { sessionStartMs: Date.now(), completions: [] };
      persist(fresh);
      set(fresh);
    },
  };
});

/* ── Derived helpers (plain functions, not hooks) ── */

/** Number of studies completed this session. */
export function getStudiesRead(): number {
  return useReadingTempoStore.getState().completions.length;
}

/** Average time between completions in ms, or null if < 2 completions. */
export function getAvgTimeMs(): number | null {
  const { completions } = useReadingTempoStore.getState();
  if (completions.length < 2) return null;
  const sorted = [...completions].sort((a, b) => a.timestamp - b.timestamp);
  const totalSpan = sorted[sorted.length - 1].timestamp - sorted[0].timestamp;
  return totalSpan / (sorted.length - 1);
}

/** Studies per hour pace, or null if < 2 completions. */
export function getPacePerHour(): number | null {
  const avg = getAvgTimeMs();
  if (avg == null || avg === 0) return null;
  return 3_600_000 / avg;
}

/** Convenience: call from studyActions without importing hooks. */
export function recordStudyCompletion(accession: string) {
  useReadingTempoStore.getState().recordCompletion(accession);
}
